import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Sparkles, Users, Target, Award, Clock, Heart, Calendar, MapPin, Phone, Mail, MessageSquare, CheckCircle, Trophy, Star, Zap, Shield, Gift, PartyPopper, Cake, Briefcase, Plane, Music, Camera, Utensils } from 'lucide-react';

const About = () => {
  const [animatedStats, setAnimatedStats] = useState([false, false, false, false]);
  const [hoveredService, setHoveredService] = useState(null);

  // Statistics with animation
  const stats = [
    { id: 1, value: '500+', label: 'Events Planned', icon: <Calendar className="h-8 w-8" />, color: 'from-yellow-400 to-pink-400' },
    { id: 2, value: '98%', label: 'Client Satisfaction', icon: <Heart className="h-8 w-8" />, color: 'from-pink-400 to-rose-400' },
    { id: 3, value: '15+', label: 'Cities Across India', icon: <MapPin className="h-8 w-8" />, color: 'from-blue-400 to-cyan-400' },
    { id: 4, value: '50+', label: 'Expert Planners', icon: <Users className="h-8 w-8" />, color: 'from-purple-400 to-pink-400' },
  ];

  // Why Choose Us points
  const whyChooseUs = [
    {
      id: 1,
      title: 'Personalized Planning',
      description: 'Every event is unique. We create customized experiences tailored to your vision and preferences.',
      icon: <Heart className="h-10 w-10" />,
      color: 'bg-pink-100 text-pink-600'
    },
    {
      id: 2,
      title: 'Stress-Free Experience',
      description: 'Sit back and relax while we handle everything from planning to execution, ensuring a seamless experience.',
      icon: <Sparkles className="h-10 w-10" />,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      id: 3,
      title: 'Creative Excellence',
      description: 'Our team of creative designers brings innovative ideas to make your event truly spectacular.',
      icon: <Award className="h-10 w-10" />,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 4,
      title: 'Attention to Detail',
      description: 'We pay attention to every small detail to ensure perfection in every aspect of your celebration.',
      icon: <Target className="h-10 w-10" />,
      color: 'bg-blue-100 text-blue-600'
    },
  ];

  // Our Services
  const services = [
    {
      id: 1,
      title: 'Wedding Planning',
      description: 'Complete wedding planning from venue selection to decor, catering, and entertainment.',
      icon: '💒',
      features: ['Venue Selection', 'Decor & Theme', 'Catering', 'Entertainment', 'Photography'],
      color: 'from-pink-400 to-rose-400'
    },
    {
      id: 2,
      title: 'Birthday Parties',
      description: 'Fun and creative birthday celebrations for all ages with personalized themes.',
      icon: '🎂',
      features: ['Theme Planning', 'Decorations', 'Entertainment', 'Cake Design', 'Party Favors'],
      color: 'from-yellow-400 to-orange-400'
    },
    {
      id: 3,
      title: 'Corporate Events',
      description: 'Professional corporate gatherings, meetings, and team-building activities.',
      icon: '🏢',
      features: ['Venue Setup', 'Catering', 'AV Equipment', 'Team Activities', 'Networking'],
      color: 'from-blue-400 to-cyan-400'
    },
    {
      id: 4,
      title: 'Destination Trips',
      description: 'Plan memorable trips and getaways with complete travel arrangements.',
      icon: '✈️',
      features: ['Travel Planning', 'Accommodation', 'Local Experiences', 'Transport', 'Guides'],
      color: 'from-green-400 to-emerald-400'
    },
    {
      id: 5,
      title: 'Anniversary Celebrations',
      description: 'Romantic and memorable anniversary parties to celebrate love and togetherness.',
      icon: '💝',
      features: ['Theme Design', 'Floral Decor', 'Music', 'Catering', 'Surprise Elements'],
      color: 'from-red-400 to-pink-400'
    },
    {
      id: 6,
      title: 'Special Occasions',
      description: 'All other celebrations including baby showers, graduations, and promotions.',
      icon: '🎉',
      features: ['Custom Planning', 'Decor', 'Entertainment', 'Food', 'Photography'],
      color: 'from-purple-400 to-indigo-400'
    },
  ];

  // Team Members - Updated with placeholder images
  const team = [
    {
      id: 1,
      name: 'Zaid Masood',
      role: 'Founder & CEO',
      description: 'With 15+ years in event management, Zaid ensures every event is magical.',
      initials: 'ZM',
      color: 'from-yellow-400 to-orange-400'
    },
    {
      id: 2,
      name: 'Hassaan Mansur',
      role: 'Creative Director',
      description: 'Hassaan brings innovative themes and designs to life with his creative vision.',
      initials: 'HM',
      color: 'from-pink-400 to-rose-400'
    },
    {
      id: 3,
      name: 'Heer',
      role: 'Operations Head',
      description: 'Heer ensures flawless execution and coordination of all events.',
      initials: 'HH',
      color: 'from-blue-400 to-cyan-400'
    },
    {
      id: 4,
      name: 'Ayesha Ejaz',
      role: 'Client Relations',
      description: 'Ayesha builds lasting relationships with clients, understanding their dreams.',
      initials: 'AE',
      color: 'from-purple-400 to-pink-400'
    },
  ];

  // Animate stats on scroll
  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          stats.forEach((_, index) => {
            setTimeout(() => {
              setAnimatedStats(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 300);
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-4 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-200 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-pink-200 rounded-full translate-x-1/3 translate-y-1/3 opacity-20"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-yellow-100 to-pink-100 rounded-2xl mb-6 animate-pulse">
              <Sparkles className="h-12 w-12 text-yellow-500" />
              <Sparkles className="h-10 w-10 text-pink-500 -ml-4" />
              <Sparkles className="h-8 w-8 text-yellow-500 -ml-4" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-yellow-500 via-pink-500 to-yellow-500 bg-clip-text text-transparent animate-gradient">
              Where Dreams Become Celebrations
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              At Jashn, we believe every celebration is a story waiting to be told. 
              Since 2015, we've been crafting unforgettable moments that turn your 
              dreams into magical realities.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-yellow-300 transform hover:-translate-y-1 transition-all duration-300">
                Start Your Journey
              </button>
              <button className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-full hover:bg-yellow-50 border border-yellow-200 transition-all duration-300">
                Meet Our Team
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats-section" className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={stat.id}
                className={`bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
                  animatedStats[index] ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                }`}
              >
                <div className={`h-16 w-16 rounded-2xl bg-gradient-to-r ${stat.color} flex items-center justify-center text-white mb-4 mx-auto`}>
                  {stat.icon}
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-pink-600 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <p className="text-gray-600">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-yellow-50 to-pink-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-white rounded-3xl p-8 shadow-2xl">
                <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
                  Our Story
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Jashn began with a simple idea: every celebration deserves to be extraordinary. 
                  What started as a small team passionate about creating beautiful events has 
                  grown into a premier event planning service trusted by thousands.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Our journey has been filled with laughter, tears of joy, and countless 
                  magical moments. We've celebrated love at weddings, cheered at birthdays, 
                  and created memories that last a lifetime.
                </p>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-yellow-600">
                    <CheckCircle className="h-5 w-5" />
                    <span>15+ Years Experience</span>
                  </div>
                  <div className="flex items-center space-x-2 text-pink-600">
                    <Trophy className="h-5 w-5" />
                    <span>Award Winning</span>
                  </div>
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-gradient-to-br from-yellow-400 to-pink-400 rounded-2xl -z-10"></div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-gradient-to-tr from-pink-400 to-rose-400 rounded-2xl -z-10"></div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              {team.map((member) => (
                <div key={member.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className={`h-20 w-20 rounded-full bg-gradient-to-r ${member.color} mb-4 mx-auto flex items-center justify-center text-white font-bold text-2xl`}>
                    {member.initials}
                  </div>
                  <h3 className="font-bold text-lg text-center mb-1">{member.name}</h3>
                  <p className="text-yellow-600 text-sm text-center mb-2">{member.role}</p>
                  <p className="text-gray-500 text-sm text-center">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Jashn */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
              Why Choose Jashn?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We go beyond event planning to create experiences that touch hearts and create memories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <div
                key={item.id}
                className="group bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className={`h-16 w-16 rounded-2xl ${item.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                <div className="mt-6 pt-6 border-t border-yellow-100">
                  <div className="flex items-center text-yellow-500">
                    <Star className="h-4 w-4 fill-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 px-4 bg-gradient-to-br from-yellow-50 to-pink-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
              Our Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From intimate gatherings to grand celebrations, we handle every detail with precision and passion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onMouseEnter={() => setHoveredService(service.id)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-4xl">{service.icon}</div>
                    <div className={`h-2 w-12 rounded-full bg-gradient-to-r ${service.color} group-hover:w-24 transition-all duration-300`}></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-3">What we include:</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium group-hover:scale-105 transition-transform duration-300"
                          style={{ transitionDelay: `${index * 50}ms` }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button className="w-full py-3 bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-yellow-200 transition-all duration-300">
                    Learn More
                  </button>
                </div>
                
                {/* Hover Effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-gradient-to-br from-yellow-50 to-pink-50 rounded-3xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Side - Contact Info */}
              <div className="p-12 bg-gradient-to-br from-yellow-500 to-pink-500 text-white">
                <h2 className="text-4xl font-bold mb-6">Get in Touch</h2>
                <p className="text-yellow-100 mb-8 text-lg">
                  Ready to plan your next celebration? We'd love to hear from you!
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold">Call Us</p>
                      <p className="text-yellow-100">+91 98765 43210</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold">Email Us</p>
                      <p className="text-yellow-100">hello@jashn.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold">Visit Us</p>
                      <p className="text-yellow-100">123 Celebration Street, Mumbai</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-12 pt-8 border-t border-white/20">
                  <h3 className="text-xl font-bold mb-4">Business Hours</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Monday - Friday</span>
                      <span>9:00 AM - 8:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday - Sunday</span>
                      <span>10:00 AM - 6:00 PM</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Side - Contact Form */}
              <div className="p-12 bg-white">
                <h3 className="text-3xl font-bold mb-8 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
                  Send Us a Message
                </h3>
                
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 mb-2">Your Name</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl border border-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2">Email Address</label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 rounded-xl border border-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 rounded-xl border border-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Event Type</label>
                    <select className="w-full px-4 py-3 rounded-xl border border-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all">
                      <option value="">Select event type</option>
                      <option value="wedding">Wedding</option>
                      <option value="birthday">Birthday</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="trip">Trip/Travel</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Your Message</label>
                    <textarea
                      rows="4"
                      className="w-full px-4 py-3 rounded-xl border border-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                      placeholder="Tell us about your dream event..."
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-yellow-200 transform hover:-translate-y-1 transition-all duration-300"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <MessageSquare className="h-5 w-5" />
                      <span>Send Message</span>
                    </span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-yellow-400 to-pink-400 rounded-3xl p-12 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Create Magic Together?
            </h2>
            <p className="text-yellow-100 text-lg mb-8">
              Let's transform your vision into an unforgettable celebration that will be cherished forever.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-white text-yellow-600 font-bold rounded-full hover:bg-yellow-50 transform hover:-translate-y-1 transition-all duration-300">
                Book a Free Consultation
              </button>
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transform hover:-translate-y-1 transition-all duration-300">
                View Our Portfolio
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;